var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable */
import { Autocomplete, Box, FormControl, Grid, InputAdornment, Paper, TextField, Tooltip, Typography, } from "@mui/material";
import { DataGrid, } from "@mui/x-data-grid";
import { MagnifyingGlass } from "@phosphor-icons/react";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { objectStatusBgColor, objectStatusMapping, objectStatusTextColor, statusTextColor, getObjectStatusColors, } from "../../../application/common/utils/column-helper";
import { convertToNLEuroFormat } from "../../../application/common/utils/helper";
import LeadsObjectApi from "../../../infrastructure/api/pages/lead-object-api";
import CustomModal from "../modal/CustomModal";
import "./ObjectModal.scss";
import { UserRolesPermissionsContext } from "../../../context/UserRolesPermissionsContext";
import { checkCreateEdit, hasOnlyReadPermission, hasPartialAccess, hasFullAccess } from "../../../application/common/utils/roles-helper";
import { Modules } from "../../../application/common/enums/screen-name";
import ContractAffectedConfirmationDialog from "../../components/ContractAffectedConfirmationDialog";
const ChangeStatusModal = ({ open, onClose, refresh, statusTree, objects, leadId, }) => {
    const [saving, setSaving] = useState(false);
    const { t: translate } = useTranslation();
    const { RolesPermissions } = useContext(UserRolesPermissionsContext);
    // Check user permission levels
    const isReadOnly = hasOnlyReadPermission(RolesPermissions, Modules.ChangeObjectStatus);
    const isPartialAccess = hasPartialAccess(RolesPermissions, Modules.ChangeObjectStatus);
    const isFullAccess = hasFullAccess(RolesPermissions, Modules.ChangeObjectStatus);
    const canChangeObjectStatus = checkCreateEdit(RolesPermissions, Modules.ChangeObjectStatus);
    // Define which statuses are RESTRICTED for partial access (blacklist approach)
    // Partial access CANNOT work with: Signed (Ondertekend), the automatic Compleet,
    // or the manual Afgerond richting planning. All other statuses are allowed
    const partialAccessRestrictedStatuses = ["Ondertekend", "Compleet", "Afgerond richting planning", "Signed", "Completed"];
    const columnsForDataGrid = [
        {
            field: "objectID",
            headerName: translate("ID"),
            sortable: true,
            align: "left",
            disableColumnMenu: true,
            headerAlign: "left",
            hideable: false,
            filterable: true,
            minWidth: 100,
        },
        {
            field: "name",
            headerName: translate("Name"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            disableColumnMenu: true,
            hideable: false,
            filterable: true,
            minWidth: 150,
        },
        {
            field: "address",
            headerName: translate("Adres/GPS"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            disableColumnMenu: true,
            hideable: false,
            filterable: true,
            minWidth: 200,
            renderCell: (params) => {
                const { street, houseNumber, addition, postalCode, city, longitude, latitude, objectFullAddress, } = params.row;
                // Prefer the stored full address; otherwise compose in Dutch format.
                let address = (objectFullAddress === null || objectFullAddress === void 0 ? void 0 : objectFullAddress.trim()) ? objectFullAddress.trim() : "";
                if (!address && (street || houseNumber || addition || postalCode || city)) {
                    address = [
                        [street, houseNumber, addition].filter(Boolean).join(" "),
                        [postalCode, city].filter(Boolean).join(" "),
                    ]
                        .filter(Boolean)
                        .join(", ");
                }
                if (address.trim()) {
                    return _jsx("span", { children: address });
                }
                else if (longitude && latitude) {
                    return (_jsxs("span", { children: [longitude, ", ", latitude] }));
                }
                else {
                    return _jsx("span", { children: "No address" });
                }
            },
        },
        {
            field: "companyRoleName",
            headerName: translate("Role"),
            sortable: true,
            align: "left",
            headerAlign: "left",
            hideable: false,
            disableColumnMenu: true,
            filterable: true,
            minWidth: 150,
        },
        {
            field: "status",
            headerName: translate("Status"),
            sortable: false,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            hideSortIcons: true,
            minWidth: 200,
            disableColumnMenu: true,
            renderCell: (params) => {
                const row = params.row;
                // Always show Level 1 status name (primary status category)
                const dynamicLabel = row.statusLevel1Name;
                const fallbackLabel = translate(`${objectStatusMapping[params.row.status]}`);
                const label = dynamicLabel || fallbackLabel;
                // Get colors based on the status name (new dynamic system) or fall back to old enum-based colors
                const colors = dynamicLabel
                    ? getObjectStatusColors(dynamicLabel)
                    : {
                        bgColor: objectStatusBgColor[params.row.status] || "#E9ECEF",
                        textColor: objectStatusTextColor[params.row.status] || "#495057",
                    };
                return (_jsx(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                        width: "100%"
                    }, children: _jsx(Box, { sx: {
                            bgcolor: colors.bgColor,
                            padding: 1,
                            mt: 1,
                            borderRadius: 2,
                            maxWidth: "calc(100% - 10px)",
                            cursor: "pointer"
                        }, children: _jsx(Tooltip, { title: label, arrow: true, children: _jsx(Typography, { variant: "h5", sx: {
                                    width: "100%",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                    color: colors.textColor,
                                }, children: label }) }) }) }));
            },
        },
        {
            field: "price",
            headerName: translate("Price"),
            sortable: false,
            align: "left",
            headerAlign: "left",
            hideable: false,
            filterable: true,
            disableColumnMenu: true,
            hideSortIcons: true,
            minWidth: 80,
            renderCell: (params) => (_jsx(Typography, { sx: {
                    color: statusTextColor[3],
                    fontWeight: 500,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }, children: convertToNLEuroFormat(params.value || "0") })),
        },
    ];
    const hiddenButtonRef = useRef(null);
    // Dynamic Object Status tree states (statusTree is now passed as prop)
    const [level1Options, setLevel1Options] = useState([]);
    const [level2Options, setLevel2Options] = useState([]);
    const [level3Options, setLevel3Options] = useState([]);
    const [level1, setLevel1] = useState("");
    const [level2, setLevel2] = useState("");
    const [level3, setLevel3] = useState("");
    // Contract affected confirmation dialog states
    const [showContractConfirmation, setShowContractConfirmation] = useState(false);
    const [affectedContracts, setAffectedContracts] = useState([]);
    const [checkingContracts, setCheckingContracts] = useState(false);
    // Grid state
    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [search, setSearch] = useState({ objectId: "", name: "" });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [level1StatusName, setLevel1StatusName] = useState({});
    const [showValidation, setShowValidation] = useState(false);
    // Calculate if the save button should be disabled
    // Button is enabled only when both conditions are met:
    // 1. Level 1 status is selected
    // 2. At least one object is selected
    const isDisabled = useMemo(() => {
        const hasSelectedStatus = level1.trim() !== "";
        const hasSelectedObjects = selectionModel.ids.size > 0;
        return !hasSelectedStatus || !hasSelectedObjects;
    }, [level1, selectionModel]);
    // Reset all form state to initial values
    // This ensures the modal starts fresh each time it's opened
    const resetModalState = () => {
        setSearch({ objectId: "", name: "" });
        setSelectionModel({ type: "include", ids: new Set() });
        setLevel1("");
        setLevel2("");
        setLevel3("");
        setLevel2Options([]);
        setLevel3Options([]);
        setPaginationModel({ page: 0, pageSize: 10 });
        setShowValidation(false);
        setShowContractConfirmation(false);
        setAffectedContracts([]);
    };
    // Check for affected contracts before proceeding with status update
    const handleSaveButtonClick = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        // Show validation errors
        setShowValidation(true);
        // Bulk status update flow
        const selectedObjectIds = Array.from(selectionModel.ids).filter(Boolean);
        const statusPathIds = [level1, level2, level3].filter(Boolean);
        if (!selectedObjectIds || selectedObjectIds.length === 0) {
            toast.warning(translate("selectAtLeastOneObject"));
            return;
        }
        if (statusPathIds.length === 0) {
            toast.warning(translate("selectStatus"));
            return;
        }
        // Validate dependent dropdown selections
        if (level1 && level2Options.length > 0 && !level2) {
            toast.warning(translate("selectReason"));
            return;
        }
        if (level2 && level3Options.length > 0 && !level3) {
            toast.warning(translate("selectFromDropdown"));
            return;
        }
        // Check if any contracts will be affected by this status change
        try {
            setCheckingContracts(true);
            const response = yield LeadsObjectApi.checkAffectedContracts({
                leadId,
                objectIds: selectedObjectIds,
                statusPathIds,
            });
            if (response.data.hasAffectedContracts) {
                // Show confirmation dialog with affected contracts
                setAffectedContracts(response.data.affectedContracts);
                setShowContractConfirmation(true);
            }
            else {
                // No contracts affected, proceed directly
                yield performStatusUpdate(selectedObjectIds, statusPathIds);
            }
        }
        catch (e) {
            toast.error(((_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) || translate("Error checking affected contracts"));
        }
        finally {
            setCheckingContracts(false);
        }
    });
    // Perform the actual status update (called after confirmation or directly)
    const performStatusUpdate = (selectedObjectIds, statusPathIds) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            setSaving(true);
            const res = yield LeadsObjectApi.bulkStatusUpdate(leadId, selectedObjectIds, statusPathIds);
            if (res.status === 200) {
                toast.success(translate("UpdatedSuccessfully"));
                refresh(); // Call refresh first
                resetModalState(); // Reset state before closing
                onClose(); // Then close modal
            }
        }
        catch (e) {
            toast.error(((_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) || translate("Error"));
        }
        finally {
            setSaving(false);
        }
    });
    // Handle confirmation dialog accept
    const handleConfirmStatusChange = () => __awaiter(void 0, void 0, void 0, function* () {
        setShowContractConfirmation(false);
        const selectedObjectIds = Array.from(selectionModel.ids).filter(Boolean);
        const statusPathIds = [level1, level2, level3].filter(Boolean);
        yield performStatusUpdate(selectedObjectIds, statusPathIds);
    });
    // Handle confirmation dialog cancel
    const handleCancelStatusChange = () => {
        setShowContractConfirmation(false);
        setAffectedContracts([]);
    };
    // Initialize status tree and use objects from parent (no API call needed)
    useEffect(() => {
        if (!open)
            return;
        // Reset state when modal opens
        resetModalState();
        if (!statusTree.length)
            return;
        // Use the pre-loaded tree from parent
        // Filter active statuses, and for partial access, exclude restricted statuses
        let filteredTree = statusTree.filter((node) => node.isActive);
        if (isPartialAccess) {
            // Partial access cannot see/select: Signed (Getekend) or Completed (Afgerond)
            // All other statuses are allowed
            filteredTree = filteredTree.filter((node) => !partialAccessRestrictedStatuses.includes(node.name));
        }
        setLevel1Options(filteredTree);
        // Use objects passed from parent (already fetched in ObjectTab)
        setRows(objects || []);
    }, [open, statusTree]); // Depend on open and statusTree
    // Update rows when objects change, with filtering for partial access
    useEffect(() => {
        if (open && objects) {
            // For partial access, filter out objects with Signed/Completed status
            if (isPartialAccess) {
                const filteredObjects = objects.filter((obj) => {
                    const status = obj.statusLevel1Name || "";
                    return !partialAccessRestrictedStatuses.includes(status);
                });
                setRows(filteredObjects);
            }
            else {
                setRows(objects);
            }
        }
    }, [objects, open, isPartialAccess]);
    const filteredRows = useMemo(() => {
        const idQuery = search.objectId.trim().toLowerCase();
        const nameQuery = search.name.trim().toLowerCase();
        return (rows || []).filter((r) => {
            var _a, _b;
            const idMatch = !idQuery || ((_a = r.objectID) === null || _a === void 0 ? void 0 : _a.toString().toLowerCase().includes(idQuery));
            const nameMatch = !nameQuery || ((_b = r.name) !== null && _b !== void 0 ? _b : "").toLowerCase().includes(nameQuery);
            return idMatch && nameMatch;
        });
    }, [rows, search]);
    const onSelectLevel1 = (id) => {
        setLevel1(id);
        const node = level1Options.find((x) => x.id === id);
        // Filter only active statuses for level 2
        setLevel2Options(((node === null || node === void 0 ? void 0 : node.children) || []).filter((child) => child.isActive));
        setLevel2("");
        setLevel3("");
        setLevel3Options([]);
        // Reset validation state when changing selection
        if (showValidation)
            setShowValidation(false);
    };
    const onSelectLevel2 = (id) => {
        setLevel2(id);
        const node = level2Options.find((x) => x.id === id);
        // Filter only active statuses for level 3
        setLevel3Options(((node === null || node === void 0 ? void 0 : node.children) || []).filter((child) => child.isActive));
        setLevel3("");
        // Reset validation state when changing selection
        if (showValidation)
            setShowValidation(false);
    };
    const onSelectLevel3 = (id) => {
        setLevel3(id);
        // Reset validation state when changing selection
        if (showValidation)
            setShowValidation(false);
    };
    return (_jsxs(CustomModal, { open: open, onClose: () => {
            // Don't allow closing while saving or checking contracts to prevent state inconsistency
            if (!saving && !checkingContracts) {
                resetModalState(); // Reset state when closing without saving
                onClose();
            }
        }, size: "lg", saving: saving || checkingContracts, handleSaveButtonClick: handleSaveButtonClick, heading: translate("changeStatus"), saveButtonText: checkingContracts ? translate("Checking...") : translate("changeStatus"), disabled: isDisabled || checkingContracts, showSaveButton: !isReadOnly && canChangeObjectStatus, children: [_jsxs(Box, { sx: {
                    p: { xs: "6px 12px", md: "24px 48px", overflow: "hidden" }
                }, children: [_jsxs("form", { children: [_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: {
                                            xs: 12,
                                            md: 6
                                        }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "medium", children: _jsx(TextField, { id: "search-objectid", label: translate("ID"), value: search.objectId, onChange: (e) => setSearch((p) => (Object.assign(Object.assign({}, p), { objectId: e.target.value }))), variant: "outlined", placeholder: translate("Search") + " " + translate("ID"), size: "medium", slotProps: {
                                                    input: {
                                                        endAdornment: (_jsx(InputAdornment, { position: "end", style: { color: "#495057" }, children: _jsx(MagnifyingGlass, {}) })),
                                                    }
                                                } }) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            md: 6
                                        }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "medium", children: _jsx(TextField, { id: "search-name", label: translate("Name"), value: search.name, onChange: (e) => setSearch((p) => (Object.assign(Object.assign({}, p), { name: e.target.value }))), variant: "outlined", placeholder: translate("Search") + " " + translate("Name"), size: "medium", slotProps: {
                                                    input: {
                                                        endAdornment: (_jsx(InputAdornment, { position: "end", style: { color: "#495057" }, children: _jsx(MagnifyingGlass, {}) })),
                                                    }
                                                } }) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            md: 4
                                        }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: false, fullWidth: true, disabled: isReadOnly, options: level1Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level1Options.find((o) => o.id === level1) || null, onChange: (_, v) => onSelectLevel1((v === null || v === void 0 ? void 0 : v.id) || ""), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Status") }))) }) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            md: 4
                                        }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: false, fullWidth: true, disabled: isReadOnly, options: level2Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level2Options.find((o) => o.id === level2) || null, onChange: (_, v) => onSelectLevel2((v === null || v === void 0 ? void 0 : v.id) || ""), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Reason"), error: showValidation &&
                                                        !!level1 &&
                                                        level2Options.length > 0 &&
                                                        !level2, helperText: showValidation &&
                                                        !!level1 &&
                                                        level2Options.length > 0 &&
                                                        !level2
                                                        ? translate("fieldRequired")
                                                        : "" }))) }) }) }), _jsx(Grid, { size: {
                                            xs: 12,
                                            md: 4
                                        }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: false, fullWidth: true, disabled: isReadOnly, options: level3Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level3Options.find((o) => o.id === level3) || null, onChange: (_, v) => onSelectLevel3((v === null || v === void 0 ? void 0 : v.id) || ""), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Keuzemenu (dropdown)"), error: showValidation &&
                                                        !!level2 &&
                                                        level3Options.length > 0 &&
                                                        !level3, helperText: showValidation &&
                                                        !!level2 &&
                                                        level3Options.length > 0 &&
                                                        !level3
                                                        ? translate("fieldRequired")
                                                        : "" }))) }) }) })] }), _jsx("button", { ref: hiddenButtonRef, type: "submit", hidden: true, children: "Submit" })] }), _jsx(Paper, { sx: {
                            width: "100%",
                            overflow: "hidden",
                            boxShadow: "none",
                            height: "100%",
                            mt: 2,
                        }, children: _jsx(DataGrid, { rows: filteredRows, getRowId: (row) => String(row.id || row.leadObjectId || row.objectID), columns: columnsForDataGrid, pagination: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, pageSizeOptions: [5, 10, 20, 50], disableRowSelectionOnClick: true, checkboxSelection: true, rowSelectionModel: selectionModel, onRowSelectionModelChange: (model) => setSelectionModel(model), keepNonExistentRowsSelected: true, isRowSelectable: (params) => {
                                // If user has full access, all rows are selectable
                                if (isFullAccess)
                                    return true;
                                // If user has partial access, exclude Signed/Completed objects from selection
                                if (isPartialAccess) {
                                    return !partialAccessRestrictedStatuses.includes(params.row.statusLevel1Name);
                                }
                                // Read-only users cannot select any rows
                                return false;
                            }, rowHeight: 80, localeText: {
                                noRowsLabel: translate("Geen rijen"),
                                noResultsOverlayLabel: translate("Geen resultaten"),
                            }, sx: {
                                border: "none",
                                fontSize: "14px",
                                // height: {
                                //   xs: "400px",
                                //   md: "calc(100% - 140px)",
                                // },
                                // maxHeight: {
                                //   md: "calc(100vh - 100px)",
                                // },
                                // "& .has-contract-row": {
                                //   backgroundColor: "#ffff",
                                // },
                                "& .MuiDataGrid-cell": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "flex",
                                    alignItems: "center",
                                    "& .MuiDataGrid-cellCheckbox": {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0,
                                    },
                                },
                                "& .MuiDataGrid-columnHeaderTitle ": {
                                    fontSize: "14px",
                                    fontWeight: 500,
                                },
                            }, sortingOrder: ["asc", "desc"] }, "leads-grid") })] }), _jsx(ContractAffectedConfirmationDialog, { open: showContractConfirmation, onClose: handleCancelStatusChange, onConfirm: handleConfirmStatusChange, affectedContracts: affectedContracts, loading: saving })] }));
};
export default ChangeStatusModal;
